/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getData } from "../../services/base.service";
import { Container, Backdrop, Fade, Grid, Box, Fab, Button, Card, CardContent } from '@mui/material';
import { MenuItemProps, PartnersTableDto, PartnersFilterType, TreeNode } from "../../types/dataTypes";
import { pulseAnimation } from '../../styles/styles';
import { initialFiltersPartners, partnerTableDefinition } from "../../utils/consts";
import ScrollTop from "../Navigation/ScrollTop";
import { Add, ArrowForward, FormatListBulleted, KeyboardArrowUp } from "@mui/icons-material";
import { useTheme } from '@mui/material/styles';
import SortFeature from "../Shared/SortFeature";
import PartnersTable from "./PartnersTable";
import PartnersFilters from "./PartnersFilters";
import UpdatePartner from "./UpdatePartner";
import AddPartner from "./AddPartner";
import CustomMenu from "../Shared/CustomMenue";
import { useNotification } from "../Shared/NotificationProvider";

type PartnersViewProps = {
  userRoles: string[];
};

function PartnersView({ userRoles }: PartnersViewProps) {
  const navigate = useNavigate();
  const { partnerID } = useParams<{ partnerID: string }>();
  
  // State
  const [data, setData] = useState<PartnersTableDto[]>([]);
  const [filteredData, setFilteredData] = useState<PartnersTableDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [filters, setFilters] = useState<PartnersFilterType>(initialFiltersPartners);
  const [isDrawerAdvancedFilterOpen, setIsDrawerAdvancedFilterOpen] = useState(false);
  const [selectedReference] = useState<string[]>([]);
  const [numCardsToDisplay, setNumCardsToDisplay] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: "number_of_collaborations", direction: "desc" });
  const [, setManageMetaDataDialogOpen] = useState(false);
  const [detailsAddDialogOpen, setDetailsAddDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isTableView] = useState<boolean>(true);
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);

  const theme = useTheme();
  const notify = useNotification();

  const accessToken = localStorage.getItem('accessToken') || '';

  // Derived state
  const detailsSelected = partnerID ? parseInt(partnerID, 10) : 0;
  const isDrawerDetailsOpen = !!partnerID && !isNaN(detailsSelected);

  // Effects
  useEffect(() => {
    fetchData();
    setFilters(initialFiltersPartners);
  }, [reload]);

  // Handle URL parameter changes
  useEffect(() => {
    if (partnerID) {
      const id = parseInt(partnerID, 10);
      if (isNaN(id)) {
        // Invalid partner ID, redirect to partners list
        navigate('/partners', { replace: true });
      }
    }
  }, [partnerID, navigate]);

  // API calls
  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await getData('partners/partnerTable');
      setData(result.partnertableDto as PartnersTableDto[]);
      setFilteredData(result.partnertableDto as PartnersTableDto[]);
      setTreeNodes(result.treeNodeList as TreeNode[]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handleMoreClick = () => {
    setNumCardsToDisplay(prev => prev + 25);
  };

  const handleDetailsUpdateDialogOpen = (id: number) => {
    console.log(`Opening details for partner ID: ${id}`);
    navigate(`/partners/${id}`);
  };

  const handleDetailsClose = () => {
    navigate('/partners');
  };

  const handleSelectReference = (id: number) => {
    // TODO: Implement selection logic
    // if (selectedReference.includes(projectNo)) {
    //   setSelectedReference(selectedReference.filter((x) => x !== projectNo));
    // } else {
    //   setSelectedReference([...selectedReference, projectNo]);
    // }
  };

  const handleManageMetaDataDialogOpen = () => {
    setManageMetaDataDialogOpen(true);
  };

  const handleManageOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleManageClose = () => {
    setAnchorEl(null);
  };

  const handleDetailsAddDialogOpen = () => {
    notify(`Create a new partner by filling the form below.`, 'info');
    setDetailsAddDialogOpen(true);
  };

  const handleDetailsAddDialogClose = () => {
    setDetailsAddDialogOpen(false);
  };

  // Computed values
  const sortedData = useMemo(() => {
    let sortableData = [...filteredData];
    if (sortConfig !== null) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [filteredData, sortConfig]);

  const menuItems: MenuItemProps[] = [
    {
      name: 'Add Partner',
      IconButton: Add,
      onClickFunction: handleDetailsAddDialogOpen,
    },
    {
      name: 'Manage Meta Data',
      IconButton: FormatListBulleted,
      onClickFunction: handleManageMetaDataDialogOpen,
      adminOnly: true,
    },
  ];


  return (
    <Container>
      <Grid>
        <Card sx={{ margin: 1, overflow: "visible" }}>
          <CardContent>
            <PartnersFilters 
              data={data} 
              setFilteredData={setFilteredData} 
              setLoading={setLoading} 
              setReload={setReload}
              filters={filters} 
              setFilters={setFilters} 
              isDrawerOpen={isDrawerAdvancedFilterOpen} 
              setIsDrawerOpen={setIsDrawerAdvancedFilterOpen}
              treeNodes={treeNodes} 
            />
            <Grid container spacing={1} justifyContent='space-between'>
              <Grid item xs={12} md={4}>
                <SortFeature
                  fieldList={partnerTableDefinition.filter((item) => item.sortable === true).map((field) => field.key)}
                  fieldIconList={partnerTableDefinition.filter((item) => item.sortable === true).map((field) => field.icon)}
                  sortConfig={sortConfig}
                  setSortConfig={setSortConfig} 
                />
              </Grid>
              <Grid item xs={12} md={1}>
                {/* Optional: Add view toggle button */}
              </Grid>
                <Grid item xs={12} md={2}>
                  <CustomMenu
                    anchorEl={anchorEl}
                    handleClose={handleManageClose}
                    menuItems={menuItems}
                    handleManageOpen={handleManageOpen}
                    userRoles={userRoles}
                  />
                  </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div id="back-to-top-anchor" />
            <ScrollTop>
              <Fab color="primary" size="small" aria-label="scroll back to top">
                <KeyboardArrowUp />
              </Fab>
            </ScrollTop>
          </Grid>

          {isTableView ? (
            <Grid container spacing={0.5} justifyContent='center'>
              <PartnersTable
                items={sortedData.slice(0, numCardsToDisplay)}
                isSelected={(projectNo: string) => selectedReference.includes(projectNo)}
                partnerID={detailsSelected}
                onDetailsClick={handleDetailsUpdateDialogOpen}
                onSelectChange={handleSelectReference}
                loading={loading}
              />
              {sortedData.length > numCardsToDisplay && (
                <Grid item xs={12} sm={12} md={12}>
                  <Box display="flex" justifyContent="center" paddingTop={20} paddingBottom={20}>
                    <Button startIcon={<ArrowForward />} variant="outlined" color="primary" onClick={handleMoreClick}>
                      Load more Partners
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid container spacing={2} justifyContent='center'>
              {sortedData.slice(0, numCardsToDisplay).map((item, index) => (
                <Grid item xs={12} sm={12} md={6} key={index}>
                  {/* TODO: Implement PartnerCard component */}
                </Grid>
              ))}
              {sortedData.length > numCardsToDisplay && (
                <Grid item xs={12} sm={12} md={12}>
                  <Box display="flex" justifyContent="center" paddingTop={20} paddingBottom={20}>
                    <Button startIcon={<ArrowForward />} variant="outlined" color="primary" onClick={handleMoreClick}>
                      Load more Partners
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {/* Partner Details Drawer */}
          {isDrawerDetailsOpen && detailsSelected > 0 && (
            <UpdatePartner 
              setLoading={setLoading} 
              accessToken={accessToken} 
              partnerID={detailsSelected} 
              setFilters={setFilters}
              isDrawerOpen={isDrawerDetailsOpen} 
              setIsDrawerDetailsOpen={handleDetailsClose}
              setReload={setReload}
            />
          )}
          {detailsAddDialogOpen && (
            <AddPartner 
            setLoading={setLoading} 
            isDrawerOpen={detailsAddDialogOpen} 
            setFilters={setFilters} 
            setIsDrawerDetailsOpen={handleDetailsAddDialogClose}
            setReload={setReload} />
          )}
        </Grid>
      </Grid>
      {/* Only overlay a spinner once we already have data on screen - the initial load is
          covered by the table's own Skeleton placeholders instead of blocking the whole page. */}
      <Backdrop open={loading && data.length > 0} style={{ zIndex: 9999, color: '#fff' }}>
        <Fade in={loading && data.length > 0}>
          <img src={theme.logo} alt="Company Logo" css={pulseAnimation} />
        </Fade>
      </Backdrop>
    </Container>
  );
}

export default PartnersView;